import { Box, Flex, Text } from "@chakra-ui/react";
import { CaretRight } from "@phosphor-icons/react";
import { getProgressColor, getProgressStyle, progressOf } from "utils/stageProgress";
import { routes } from "config/paths";
import { useConfirmedNavigate } from "../../../../context/Mutation/useConfirmedNavigate";

/**
 * The same workstream's other years, under the one in view.
 *
 * Filtered to the same `questionGroup`: the page is about one workstream, and offering a jump to a different
 * one's year would be a different workstream's work presented as this one's history.
 *
 * Navigates through `useConfirmedNavigate` rather than a bare `navigate`, so leaving with unsaved notes warns.
 * v1's equivalent uses a bare navigate and loses the edit silently — deliberately not copied.
 */
export default function PriorYearCards({ questions, answerModifiedFlag }) {
  const confirmedNavigate = useConfirmedNavigate();

  if (!questions?.length) return null;

  const open = (question) =>
    confirmedNavigate(`${routes.foundationDataV2Answer}?id=${question.id}`, {
      confirm: answerModifiedFlag,
      title: "Unsaved changes?",
      message:
        "It looks like you are navigating away with unsaved changes. Continuing will mean these changes will be lost.",
      confirmText: "Discard Changes",
      cancelText: "Cancel",
    });

  return (
    <Flex direction="column" gap={3}>
      {questions.map((question) => (
        <Box
          key={question.id}
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="xl"
          p={5}
          bg="white"
          cursor="pointer"
          _hover={{ borderColor: "gray.300", shadow: "sm" }}
          onClick={() => open(question)}
        >
          <Flex justify="space-between" align="flex-start">
            <Box flex="1">
              <Text fontSize="md" fontWeight="bold" color="gray.800">
                {question.question}
              </Text>
              {question.description ? (
                <Text fontSize="sm" color="gray.500" mt={1} noOfLines={2}>
                  {question.description}
                </Text>
              ) : null}
            </Box>
            <Box color="gray.400" flexShrink={0} ml={4}>
              <CaretRight size={20} />
            </Box>
          </Flex>

          <Text fontSize="xs" fontWeight="medium" color="gray.600" mt={3}>
            {question.stage}
          </Text>
          <Box h="6px" borderRadius="full" bg="gray.100" overflow="hidden" mt={2}>
            <Box
              h="100%"
              w={`${progressOf(question.stageType)}%`}
              borderRadius="full"
              bg={getProgressColor(question.stageType)}
              sx={getProgressStyle(question.stageType)}
            />
          </Box>
        </Box>
      ))}
    </Flex>
  );
}
